/**
 * Tvar formulare „Zalozit zakazku" (drive `lib/deals-store.ts`). Krok S3:
 * koncept uz se NEUKLADA do uloziste prohlizece — obrazovka drzi rozpracovany
 * stav v pameti komponenty a tlacitkem zaklada skutecnou zakazku
 * (`POST /v1/cases`). Zustavaji tu proto jen typy, zadne uloziste.
 *
 * Krok UXZ1: zalozeni uz NESPOUSTI provereni (to je dalsi krok z detailu
 * zakazky), takze tady zije i prevod druhu/zameru na to, co zna
 * `lib/case-create.ts` — jedno misto pro obe tabulky.
 */

import type { CaseClientRole, NewCaseType, NewPropertyKind } from './case-create.js';

/** Krok UXZ1: pribyla „Komerční" (zadani bod 1, pet kompaktnich voleb). */
export type DealKind = 'LAND' | 'HOUSE' | 'APARTMENT' | 'COMMERCIAL' | 'OTHER';
export type DealIntent = 'SALE' | 'RENT' | 'INQUIRY';
export type DealClientType = 'PERSON' | 'COMPANY';

export interface DealClient {
  readonly type: DealClientType;
  /** Jmeno soukrome osoby — pouze pri type === 'PERSON'. */
  readonly personName: string;
  /** Nazev firmy — pouze pri type === 'COMPANY', doplni se z ARES nebo rucne. */
  readonly companyName: string;
  readonly ico: string;
  readonly registeredAddress: string;
  /** true = nazev/adresa doplneny z ARES (zobrazuje potvrzeni "nalezeno v ARES"). */
  readonly aresVerified: boolean;
  /**
   * Kontakt klienta (krok UXP1, nalez P1-1). Jde do `parties.contacts`, takze
   * magic link smlouvy a faktura uz e-mail nemusi chtit znovu.
   */
  readonly email: string;
  readonly phone: string;
}

export interface DealAddress {
  /** Text adresy — z napovedy Mapy.cz nebo rucne zadany. */
  readonly query: string;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly source: 'mapy' | 'manual';
}

/** Rozpracovany pruvodce. Zadne `id`/`number` — cislo dava az server pri zalozeni. */
export interface DealDraft {
  readonly kind: DealKind;
  readonly intent: DealIntent;
  readonly address: DealAddress;
  readonly parcelNumber: string;
  readonly cadastralArea: string;
  /** Cislo listu vlastnictvi. */
  readonly landRegistryNumber: string;
  readonly listingUrl: string;
  /**
   * Krok B1: cislo jednotky (bytu) podle katastru. U pozemku a domu zustava
   * prazdne - identitu tam nese parcela.
   */
  readonly unitNumber: string;
  /**
   * Krok UXP4a bod 4 (nalez P2-17): DISPOZICE bytu („3+1", „2+kk"). Formular
   * u bytu nabizel parcelu, ale ne to, cim se byt popisuje. Zakazka pri
   * zalozeni vlastni pole na dispozici nema, takze jde — stejne jako parcela,
   * katastralni uzemi a cislo jednotky — do `identityLine()` v popisu.
   */
  readonly layout: string;
  /** „Zadani maklere" — uklada se jako `dd_case_inputs` (FREE_TEXT tvrzeni). */
  readonly notes: string;
  readonly client: DealClient;
  readonly priceExpectation: string;
  /**
   * Krok PROP3 bod 3: `true` = cislo z pole „Cenová představa" je NABIDKOVA
   * cena (`asking_price`, inzerat a verejna stranka). Vychozi `false` = jen
   * predstava majitele; cislo z prvniho hovoru neni cena, pod kterou kancelar
   * nemovitost nabizi.
   */
  readonly priceIsAsking: boolean;
}

/**
 * Druh nemovitosti, nad kterou zakazka vznika (`POST /v1/properties`).
 *
 * `OTHER` = `complex` (soubor nemovitosti): prevod na dum nebo pozemek by
 * tvrdil o nemovitosti neco, co makler nerekl. Provereni takovy druh nezna,
 * takze detail zakazky druh nepredvyplni a makler ho u prvniho behu vybere
 * (`DD_KIND_BY_PROPERTY_KIND` v `routes/zakazka-detail.tsx`). „Komerční" je
 * budova — provereni ji pak navrhne jako dum a makler to muze zmenit
 * (ROZHODNUTI UXZ1-DRUHY).
 */
export const PROPERTY_KIND_BY_DEAL_KIND: Readonly<Record<DealKind, NewPropertyKind>> = {
  LAND: 'land',
  HOUSE: 'building',
  APARTMENT: 'unit',
  COMMERCIAL: 'building',
  OTHER: 'complex',
};

/** Zamer (prodej/pronajem) na druh zakazky z OpenAPI kontraktu. */
export const CASE_TYPE_BY_DEAL_INTENT: Readonly<Record<DealIntent, NewCaseType>> = {
  SALE: 'brokerage_sale',
  RENT: 'brokerage_rent',
  INQUIRY: 'brokerage',
};

/**
 * Role klienta podle zameru (krok UXP1, nalez P1-1): kdo zakazku zadal, je
 * pri prodeji prodavajici a pri pronajmu pronajimatel. `INQUIRY` ma vlastni
 * obrazovku (poptavka), takze sem chodi jen pro uplnost vyctu.
 */
export const CLIENT_ROLE_BY_DEAL_INTENT: Readonly<Record<DealIntent, CaseClientRole>> = {
  SALE: 'seller',
  RENT: 'landlord',
  INQUIRY: 'seller',
};

export function emptyDealClient(): DealClient {
  return {
    type: 'PERSON',
    personName: '',
    companyName: '',
    ico: '',
    registeredAddress: '',
    aresVerified: false,
    email: '',
    phone: '',
  };
}

export function emptyDealAddress(): DealAddress {
  return { query: '', lat: null, lon: null, source: 'manual' };
}
